import { ISmartCanvasLayer, ISmartCanvasOptions } from 'src/modules/shared/models/interfaces/smart-canvas.interface';
import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { IFilePageAndWords } from 'src/modules/shared/models/interfaces/files.interface';
import { IPageDimensions } from 'src/modules/shared/models/interfaces/page-dimensions.interface';
import { EventEmitterService } from 'src/modules/shared/services/event-emitter.service';
import { Calcs } from 'src/modules/shared/utilities/calculations';
import { BehaviorSubject, Subscription } from 'rxjs';
import { SmartCanvasHighlightedArea } from 'src/modules/shared/models/smart-canvas.model';
import { ClassificationParameters, RobotSentence, RobotWordClick } from 'src/modules/shared/models/robots.model';
import {
  IClassificationParameters,
  IClassificationParametersPage,
} from 'src/modules/shared/models/interfaces/classification-parameters.interface';
import { filter } from 'rxjs/operators';

declare const Konva: any;

@Component({
  selector: 'hcr-smart-canvas-highlights',
  template: '',
})
export class SmartCanvasHighlightsComponent implements OnChanges, OnInit {
  @Input() selectedFilePagesAndWords: IFilePageAndWords[];
  @Input() selectedRobot: ClassificationParameters;
  @Input() smartCanvasOptions: ISmartCanvasOptions;
  @Input() stagePageSizes: IPageDimensions[];
  @Input() konvaStage: any; //Konva.Stage;
  @Input() layer: ISmartCanvasLayer;

  private _groups: { [key: string]: { data: SmartCanvasHighlightedArea[]; konvaGroup: any } } = {};
  private _$ready = new BehaviorSubject<boolean>(false);
  private _$readySubscription: Subscription;

  constructor(private _eventEmitterService: EventEmitterService) {
    this._eventEmitterService.get<SmartCanvasHighlightedArea>('add-canvas-highlight').subscribe((data) => {
      this._addHighlight(data);
    });

    this._eventEmitterService.get<SmartCanvasHighlightedArea>('update-canvas-highlight').subscribe((data) => {
      this._updateHighlight(data);
    });

    this._eventEmitterService
      .get<{ data: RobotSentence | RobotWordClick; selectedDocumentIndex: number }>('fortify-canvas-highlight')
      .subscribe((data) => {
        if (data.data) {
          this._fortifyHighlight(data.data, data.selectedDocumentIndex);
        }
      });

    this._eventEmitterService.get<void>('weaken-canvas-highlight').subscribe(() => {
      this._normalizeHighlights();
    });

    this._eventEmitterService.get<number>('remove-canvas-highlight').subscribe((id) => {
      this._removeHighlight(id);
    });

    this._eventEmitterService.get<string>('remove-canvas-group-highlight').subscribe((id) => {
      this._removeGroupHighlight(id);
    });

    this._eventEmitterService.get<string>('hide-canvas-group-highlight').subscribe((id) => {
      this._hideGroupHighlight(id);
    });

    this._eventEmitterService.get<string>('show-canvas-group-highlight').subscribe((id) => {
      this._showGroupHighlight(id);
    });
  }

  private _getHighlightedPoints(highlightedArea: SmartCanvasHighlightedArea, documentIndex: number): number[] {
    if (highlightedArea && highlightedArea.boundingBoxRelative) {
      const points = Calcs.boundingBoxInchToPixelsRelative(
        highlightedArea.boundingBoxRelative.map((point, index) => {
          /// TODO: Com certeza tem uma forma melhor de fazer isso...
          if (index === 2 || index === 4 || index === 5 || index === 7) {
            if (!this.smartCanvasOptions.customStyles.showHighlightThreshold) return point;
            else return point + highlightedArea.thresholdPercentage;
          } else {
            if (!this.smartCanvasOptions.customStyles.showHighlightThreshold) return point;
            else return point - highlightedArea.thresholdPercentage;
          }
        }),
        {
          width: this.selectedFilePagesAndWords[documentIndex].width,
          height: this.selectedFilePagesAndWords[documentIndex].height,
          orientation: this.selectedFilePagesAndWords[documentIndex].angle,
          unit: this.selectedFilePagesAndWords[documentIndex].unit,
        },
        this.stagePageSizes[documentIndex].width
      );
      return points;
    }
    return [];
  }

  private _normalizeHighlights(): void {
    Object.entries(this._groups).forEach((key) => {
      key[1].data.forEach((highlightedArea) => {
        highlightedArea.poly.forEach((poly) => {
          poly.opacity((highlightedArea.layoutOptions.opacity = this.smartCanvasOptions.customStyles.sentences.opacity));
        });
      });
    });
  }

  private _weakenOthersHighlights(): void {
    Object.entries(this._groups).forEach((key) => {
      key[1].data.forEach((highlightedArea) => {
        highlightedArea.poly.forEach((poly) => {
          poly.opacity(this.smartCanvasOptions.customStyles.enforceHighlight.othersHighlights.opacity);
        });
      });
    });
  }

  private _fortifyHighlight(data: RobotSentence | RobotWordClick, selectedDocumentIndex: number): void {
    if (this.smartCanvasOptions.customStyles.enforceHighlight.enable) {
      let highlight: SmartCanvasHighlightedArea;

      if (data instanceof RobotSentence) {
        highlight = this._groups[`sentences-${selectedDocumentIndex}`]?.data.find((highlight) => {
          return data._id === highlight.id;
        });
      } else {
        highlight = this._groups[`wordclicks-${selectedDocumentIndex}`]?.data.find((highlight) => {
          return data._id === highlight.id;
        });
      }

      //Enfraquece outros highlights
      this._weakenOthersHighlights();

      //Fortalece highlight da sentence selecionada
      if (highlight) {
        highlight.poly.forEach((poly) => {
          poly.opacity(highlight.layoutOptions.opacity + this.smartCanvasOptions.customStyles.enforceHighlight.opacity);
        });
      }
    }
  }

  private _addHighlight(highlight: SmartCanvasHighlightedArea): void {
    let group = this._groups[highlight.groupName];
    this._removeHighlight(highlight.id, highlight.groupName);

    if (!group) {
      group = this._groups[highlight.groupName] = {
        data: [],
        konvaGroup: new Konva.Group(),
      };

      this.layer.konvaLayer.add(group.konvaGroup);
    }

    this.selectedFilePagesAndWords.forEach((page, index) => {
      if (this.stagePageSizes[index]) {
        const points = this._getHighlightedPoints(highlight, index);
        const poly = new Konva.Line({
          points: points,
          originalFill: highlight.layoutOptions.fill,
          fill: highlight.layoutOptions.fill,
          closed: true,
          strokeWidth: highlight.layoutOptions.strokeWidth,
          stroke: highlight.layoutOptions.fill,
          opacity: highlight.layoutOptions.opacity,
          lineJoin: 'round',
          lineCap: 'round',
          data: highlight,
          x: this.stagePageSizes[index].width * index + this.smartCanvasOptions.pageGutter * index,
          y: 0,
        });

        poly.listening(false);
        group.konvaGroup.add(poly);
        highlight.poly.push(poly);
      }
    });

    group.data.push(highlight);
  }

  private _updateHighlight(updatedHighlight: SmartCanvasHighlightedArea): void {
    const highlight = this._groups[updatedHighlight.groupName]?.data.find((highlight) => {
      return highlight.id === updatedHighlight.id;
    });

    if (highlight) {
      highlight.boundingBoxRelative = updatedHighlight.boundingBoxRelative;
      highlight.layoutOptions = updatedHighlight.layoutOptions;
      highlight.thresholdPercentage = updatedHighlight.thresholdPercentage;

      this.selectedFilePagesAndWords.forEach((page, index) => {
        if (this.stagePageSizes[index]) {
          const points = this._getHighlightedPoints(updatedHighlight, index);
          highlight.poly.forEach((poly) => {
            poly.points(points);
            poly.fill(highlight.layoutOptions.fill);
            poly.opacity(highlight.layoutOptions.opacity);
          });
        }
      });
    }
  }

  private _removeHighlight(id: number, searchGroupName?: string): void {
    const highlights = Object.keys(this._groups).map((groupName) => {
      if ((searchGroupName && searchGroupName === groupName) || !searchGroupName) {
        return this._groups[groupName].data.find((highlight) => {
          return highlight.id === id;
        });
      } else {
        return null;
      }
    });

    for (const highlight of highlights) {
      if (highlight) {
        highlight.poly.forEach((poly) => {
          poly.destroy();
        });

        this._groups[highlight.groupName].data = this._groups[highlight.groupName].data.filter((highlight) => {
          return highlight.id !== id;
        });
      }
    }
  }

  private _hideGroupHighlight(groupNane: string): void {
    const group = this._groups[groupNane];

    if (group) {
      group.konvaGroup.visible(false);
    }
  }

  private _showGroupHighlight(groupNane: string): void {
    const group = this._groups[groupNane];

    if (group) {
      group.konvaGroup.visible(true);
    }
  }

  private _removeGroupHighlight(groupNane: string): void {
    const group = this._groups[groupNane];

    if (group) {
      group.konvaGroup.destroy();
      delete this._groups[groupNane];
    }
  }

  private _renderRobotAreas(): void {
    if (this.selectedRobot && !this._$readySubscription) {
      this._$readySubscription = this._$ready.pipe(filter((ready) => ready)).subscribe(() => {
        let pages: (IClassificationParameters | IClassificationParametersPage)[] = [this.selectedRobot];

        if (this.selectedRobot.pages) {
          pages = pages.concat(this.selectedRobot.pages);
        }

        pages.forEach((page, index) => {
          // Registra a exibição das sentenças
          page.sentences.forEach((sentence) => {
            this._addHighlight(
              new SmartCanvasHighlightedArea(
                `sentences-${index}`,
                new RobotSentence(sentence),
                {
                  fill: this.smartCanvasOptions.customStyles.sentences.fill,
                  opacity: this.smartCanvasOptions.customStyles.sentences.opacity,
                },
                sentence.identificationThreshold
              )
            );
          });

          // Registra a exibição dos word clicks
          page.wordClicks.forEach((wordclick) => {
            this._addHighlight(
              new SmartCanvasHighlightedArea(
                `wordclicks-${index}`,
                new RobotWordClick(wordclick),
                {
                  fill: this.smartCanvasOptions.customStyles.wordClicks.fill,
                  opacity: this.smartCanvasOptions.customStyles.wordClicks.opacity,
                },
                wordclick.extractionThreshold
              )
            );
          });
        });
      });
    }
  }

  ngOnInit(): void {
    this.konvaStage.off('mousemove.canvas-highlight').on('mousemove.canvas-highlight', (e) => {
      // console.log(e);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.stagePageSizes || changes.selectedFilePagesAndWords) {
      Object.keys(this._groups).forEach((groupName) => {
        this._groups[groupName].konvaGroup.destroy();
      });

      this._groups = {};
      this._$ready.next(false);

      if (this.selectedFilePagesAndWords?.length > 0 && this.stagePageSizes?.length > 0) {
        this._$ready.next(true);
        this._renderRobotAreas();
      }
    }

    if (changes.selectedRobot) {
      this._$readySubscription?.unsubscribe();
      this._$readySubscription = null;
      this._renderRobotAreas();
    }
  }
}
